import {
  ActionList,
  ActionListItem,
  Box,
  ErrorBoundary,
  type IconName,
  Separator,
  Skeleton,
} from "@prismicio/editor-ui"
import { Suspense } from "react"
import { Link, type To } from "react-router-dom"

import { prismicDocsUrl } from "../../app/constants"
import { useSharedOnboardingGuideFlag } from "../../app/integrations/AmplitudeExperiments"
import { getBaseRepositoryUrl } from "../../app/routing"
import { accessSettingsPermission } from "../../app/services/permissions"
import { useRepository } from "../../app/services/repository"
import { useMyRole } from "../../app/services/role"
import { useHasCustomTypes } from "../DocumentListBody/useHasCustomTypes"
import { TeamSpacesList } from "../teamSpaces/TeamSpacesList"
import { DocumentListSharedOnboarding } from "./DocumentListSharedOnboarding"

export type SelectedMenuItem = "migration" | "main-space"

interface DocumentListMenuProps {
  selectedMenuItem?: SelectedMenuItem
}

export function DocumentListMenu(props: DocumentListMenuProps) {
  const { selectedMenuItem } = props

  return (
    <Suspense fallback={<ListMenuItemSkeleton count={1} />}>
      <DocumentListMenuContent selectedMenuItem={selectedMenuItem} />
    </Suspense>
  )
}

export function DocumentListMenuContent(props: DocumentListMenuProps) {
  const { selectedMenuItem } = props

  // some menu items will be hidden while the onboarding process is active
  // we determine if the onboarding is active by the available custom types
  const hasCustomTypes = useHasCustomTypes()

  return (
    <Box flexDirection="column" flexGrow={1} justifyContent="space-between">
      <ActionList variant="borderless" sx={{ flexGrow: 1 }}>
        {hasCustomTypes && (
          <>
            <Separator style="dashed" sx={{ marginBottom: 8 }} />
            <TeamSpacesList />
          </>
        )}
      </ActionList>
      <ActionList variant="borderless">
        <Separator style="dashed" sx={{ marginTop: 8, marginBottom: 8 }} />
        <SideMenuItem iconName="image" label="Media library" to="/medias" />
        <SideMenuItem
          iconName="migrationRelease"
          label="Migration release"
          to="/migration"
          selected={selectedMenuItem === "migration"}
        />
        <ErrorBoundary>
          <Suspense fallback={<ListMenuItemSkeleton count={1} />}>
            <SharedOnboardingGuideMenuItem />
          </Suspense>
        </ErrorBoundary>
        <Separator style="dashed" sx={{ marginTop: 8, marginBottom: 8 }} />
        <ErrorBoundary>
          <Suspense fallback={<ListMenuItemSkeleton count={4} />}>
            <SettingPermissionMenuItems />
          </Suspense>
        </ErrorBoundary>
      </ActionList>
    </Box>
  )
}

function GetStartedDocsItem() {
  return (
    <SideMenuItem
      iconName="menuBook"
      label="Documentation"
      to={prismicDocsUrl}
      newTab
    />
  )
}

export function SharedOnboardingGuideMenuItem() {
  const isEligible = useSharedOnboardingGuideFlag() === "with-shared-onboarding"

  if (!isEligible) return <GetStartedDocsItem />

  return <DocumentListSharedOnboarding fallback={<GetStartedDocsItem />} />
}

export function SettingPermissionMenuItems() {
  const baseRepoUrl = getBaseRepositoryUrl()
  const { quotas } = useRepository()

  const repositoryRole = useMyRole({})
  const canAccessSettings = accessSettingsPermission[repositoryRole]

  // some menu items will be hidden while the onboarding process is active
  // we determine if the onboarding is active by the available custom types
  const hasCustomTypes = useHasCustomTypes()

  if (!canAccessSettings) return null

  const inviteUrl = `${baseRepoUrl}/settings/users`
  const settingUrl = `${baseRepoUrl}/settings/repository`
  const customTypeBuilderUrl = `${baseRepoUrl}/masks`

  return (
    <>
      {hasCustomTypes && (
        <SideMenuItem iconName="invite" label="Invite" to={inviteUrl} />
      )}
      {!quotas.slicemachineEnabled && (
        <SideMenuItem
          iconName="database"
          label="Custom type builder"
          to={customTypeBuilderUrl}
        />
      )}
      <SideMenuItem iconName="settings" label="Settings" to={settingUrl} />
    </>
  )
}

interface SideMenuItemProps {
  iconName: IconName
  label: string
  to: To
  selected?: boolean
  newTab?: boolean
}

export function SideMenuItem(props: SideMenuItemProps) {
  const { iconName, label, to, selected, newTab = false } = props

  return (
    <ActionListItem
      textVariant="normal"
      backgroundColor="transparent"
      startIcon={iconName}
      selected={selected}
      asChild
    >
      <Link to={to} target={newTab ? "_blank" : undefined}>
        {label}
      </Link>
    </ActionListItem>
  )
}

interface ListMenuItemSkeletonProps {
  count: number
}

export function ListMenuItemSkeleton(props: ListMenuItemSkeletonProps) {
  const { count } = props
  return (
    <Box flexDirection="column" gap={16}>
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} height={48} width="100%" />
      ))}
    </Box>
  )
}
